import { IcoChevronDown16 } from '@onefootprint/icons';
import * as RadixPopover from '@radix-ui/react-popover';
import { cx } from 'class-variance-authority';
import { AnimatePresence, easeOut, motion } from 'framer-motion';
import * as React from 'react';

import Chip from '../chip';

export type InputProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  selectedValues: string[];
  options: Array<{ label: string; value: string; suffix?: React.ReactNode }>;
  placeholder: string;
  hasError: boolean;
  disabled: boolean;
  isPopoverOpen: boolean;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  toggleOption: (value: string) => void;
};

export const Input = React.forwardRef<HTMLButtonElement, InputProps>(
  (
    {
      selectedValues,
      options,
      placeholder = 'Select options',
      hasError = false,
      disabled = false,
      isPopoverOpen,
      onKeyDown,
      toggleOption,
      ...props
    },
    ref,
  ) => {
    return (
      <RadixPopover.Trigger {...props} ref={ref} tabIndex={0} asChild>
        <button
          className={cx(
            'w-full min-w-[240px]',
            'flex items-center justify-between w-full h-full p-1 border rounded-default group min-h-9 gap-x-2 focus-visible:outline-[var(--text-color-accent)] text-body-2',
            {
              'border-primary hover:border-primary': !hasError && !disabled,
              'border-tertiary bg-secondary cursor-default': disabled,
              'cursor-pointer': !disabled,
              'border-error': hasError,
            },
          )}
          tabIndex={0}
          onKeyDown={onKeyDown}
          aria-pressed={isPopoverOpen}
          type="button"
          aria-haspopup="listbox"
          aria-controls="multi-select-content"
          aria-expanded={isPopoverOpen}
          disabled={disabled}
          data-disabled={disabled}
        >
          <motion.div className="flex flex-wrap gap-1">
            <AnimatePresence mode="popLayout">
              {selectedValues.length > 0 ? (
                selectedValues.map(value => {
                  const option = options.find(o => o.value === value);
                  return (
                    <motion.div
                      key={value}
                      layout
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{
                        duration: 0.2,
                        ease: easeOut,
                      }}
                    >
                      <Chip label={option?.label} suffix={option?.suffix} value={value} toggleOption={toggleOption} />
                    </motion.div>
                  );
                })
              ) : (
                <span className="flex items-center mx-3 text-body-2 text-tertiary">{placeholder}</span>
              )}
            </AnimatePresence>
          </motion.div>
          <motion.div
            className="flex items-center justify-between mr-2"
            animate={{ rotate: isPopoverOpen ? 180 : 0 }}
            transition={{ duration: 0.2, ease: easeOut }}
          >
            <IcoChevronDown16 color={disabled ? 'tertiary' : 'primary'} />
          </motion.div>
        </button>
      </RadixPopover.Trigger>
    );
  },
);

export default Input;
